import * as Sentry from "@sentry/react";

import {
  isRouteErrorResponse,
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useParams,
  useRouteError,
} from "@remix-run/react";
import { Footer } from "~/components/Footer";
import { Header } from "~/components/Header";
import { Banner } from "./components/Banner";
import { RouteProgressBar } from "./components/RouteProgressBar";
import "./styles/globals.css";
import "./utils/init-sentry";
import { useEffect, useRef } from "react";

import { IconRefresh } from "@tabler/icons-react";

import { app } from "./utils/init-firebase";

import "prismjs/themes/prism.css";

export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    window.gtag("event", "page_view", {
      page_title: window.document.title,
      page_path: window.location.href,
    });
  }, [location.pathname, location.search]);

  return (
    <html lang="en" className="bg-background text-foreground">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta
          name="description"
          content="Find and compare the best open-source projects"
        />
        <link rel="sitemap" href="/sitemap-index.xml" />
        <link rel="stylesheet" href="https://rsms.me/inter/inter.css" />
        <meta
          property="og:image"
          content="https://cdn.builder.io/api/v1/image/assets%2FYJIGb4i01jvw0SRdL5Bt%2F2450dec5221240ada81822a5320ebb32"
        />
        <Meta />
        <Links />
        {/* Google Analytics */}
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-1NWVPJJMQH"
        ></script>
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'G-1NWVPJJMQH');
            `,
          }}
        />
      </head>
      <body className="flex flex-col min-h-screen dark:bg-background dark:text-foreground">
        <div className="sticky top-0 z-50 bg-background flex flex-col">
          <Banner />
          <RouteProgressBar />
          <Header />
        </div>
        <main className="flex-grow max-md:overflow-x-hidden">{children}</main>
        <Footer />
        <ScrollRestoration />
        <Scripts />
        <LiveReload />
        <script
          dangerouslySetInnerHTML={{
            __html: `
          (function() {
            const theme = localStorage.getItem('theme') || 'light';
            document.documentElement.classList.toggle('dark', theme === 'dark');
          })();
        `,
          }}
        />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

export function ErrorBoundary() {
  const error = useRouteError();
  console.error(error);

  let errorStack = "";
  if (isRouteErrorResponse(error)) {
    errorStack = `${error.status} ${error.statusText}`;
  } else if (error instanceof Error) {
    errorStack = error.stack || "";
  }

  useEffect(() => {
    Sentry.captureException(error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLocalhost =
    typeof window !== "undefined" && window.location.hostname === "localhost";

  return (
    <div className="container mx-auto px-4 md:px-12 sm:px-6 lg:px-8 py-12">
      <h1 className="text-4xl font-bold mb-6 text-primary">
        Oops! Something went wrong
      </h1>

      <p className="text-muted-foreground mb-6">
        Don&apos;t worry, it&apos;s not you, it&apos;s us!
      </p>
      <button
        onClick={() => window.location.reload()}
        className="bg-primary text-primary-foreground hover:bg-primary/90 py-2 px-4 rounded-md flex items-center"
      >
        <IconRefresh className="mr-2 h-4 w-4" /> Refresh Page
      </button>
      <p className="mt-6 text-muted-foreground">
        If the problem persists, please try again later.
      </p>
      {isLocalhost && (
        <>
          {errorStack && (
            <pre className="bg-muted p-4 mt-6 rounded-md overflow-x-auto mb-4">
              <code>{errorStack}</code>
            </pre>
          )}
        </>
      )}
    </div>
  );
}
